import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Slider,
  Divider,
  Chip,
  FormControl,
  InputLabel,
} from '@mui/material';
import TooltipIconButton from '../../../../components/TooltipIconButton';
import {
  Close,
  Add,
  Delete,
  CallSplit,
  LocalFireDepartment,
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import type { SubtitleEntry } from '../useCanvasStore';

const FONTS = ['Montserrat', 'Poppins', 'Inter', 'Oswald', 'Merriweather', 'Roboto Condensed'];
const POSITIONS = [
  { label: 'Bottom Center', x: undefined, y: 85 },
  { label: 'Top Center', x: undefined, y: 5 },
  { label: 'Center', x: undefined, y: 45 },
  { label: 'Bottom Left', x: 5, y: 85 },
  { label: 'Bottom Right', x: 75, y: 85 },
];

interface Props {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  subtitles: SubtitleEntry[];
  onSave: (subtitles: SubtitleEntry[]) => void;
  onBurn: (subtitles: SubtitleEntry[]) => void;
  isBurning?: boolean;
}

export default function CaptionEditorDialog({ open, onClose, videoUrl, subtitles: initialSubtitles, onSave, onBurn, isBurning }: Props) {
  const [subs, setSubs] = useState<SubtitleEntry[]>([]);
  const [selected, setSelected] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Global style
  const [globalStyle, setGlobalStyle] = useState({
    fontSize: 48,
    fontFamily: 'Montserrat',
    color: '#ffffff',
    backgroundColor: '#000000aa' as string | undefined,
    strokeColor: '#000000',
    strokeWidth: 2,
    positionPreset: 0,
    styleMode: 'box' as 'box' | 'outline',
  });

  // Init subs when dialog opens
  useEffect(() => {
    if (open) {
      setSubs(initialSubtitles.map((s, i) => ({
        ...s,
        id: s.id || `sub-${i}`,
        fontSize: s.fontSize || 48,
        fontFamily: s.fontFamily || 'Montserrat',
        color: s.color || '#ffffff',
        backgroundColor: s.backgroundColor || '#000000aa',
        strokeColor: s.strokeColor || '#000000',
        strokeWidth: s.strokeWidth ?? 2,
      })));
      setSelected(0);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [open, initialSubtitles]);

  // Attach video event listeners via ref callback pattern
  useEffect(() => {
    if (!open) return;

    // Poll for video ref readiness (dialog animation may delay mount)
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      clearInterval(interval);

      const onTime = () => setCurrentTime(video.currentTime * 1000);
      const onDuration = () => setDuration(video.duration * 1000);
      const onPlay = () => setIsPlaying(true);
      const onPause = () => setIsPlaying(false);

      video.addEventListener('timeupdate', onTime);
      video.addEventListener('loadedmetadata', onDuration);
      video.addEventListener('durationchange', onDuration);
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);

      // If already loaded
      if (video.duration) setDuration(video.duration * 1000);

      return () => {
        video.removeEventListener('timeupdate', onTime);
        video.removeEventListener('loadedmetadata', onDuration);
        video.removeEventListener('durationchange', onDuration);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('pause', onPause);
      };
    }, 100);

    return () => clearInterval(interval);
  }, [open, videoUrl]);

  const updateSub = useCallback((index: number, patch: Partial<SubtitleEntry>) => {
    setSubs(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
  }, []);

  const deleteSub = useCallback((index: number) => {
    setSubs(prev => prev.filter((_, i) => i !== index));
    setSelected(s => Math.max(0, Math.min(s, subs.length - 2)));
  }, [subs.length]);

  const addSub = useCallback(() => {
    const pos = POSITIONS[globalStyle.positionPreset];
    const lastEnd = subs.length > 0 ? subs[subs.length - 1].endMs : 0;
    const newSub: SubtitleEntry = {
      id: `sub-${Date.now()}`,
      text: '',
      startMs: lastEnd,
      endMs: lastEnd + 2000,
      fontSize: globalStyle.fontSize,
      fontFamily: globalStyle.fontFamily,
      color: globalStyle.color,
      backgroundColor: globalStyle.styleMode === 'box' ? globalStyle.backgroundColor : undefined,
      strokeColor: globalStyle.strokeColor,
      strokeWidth: globalStyle.styleMode === 'outline' ? Math.max(globalStyle.strokeWidth, 4) : globalStyle.strokeWidth,
      x: pos.x,
      y: pos.y,
    };
    setSubs(prev => [...prev, newSub]);
    setSelected(subs.length);
  }, [subs, globalStyle]);

  const splitSub = useCallback((index: number) => {
    const sub = subs[index];
    if (!sub) return;
    const midMs = Math.round((sub.startMs + sub.endMs) / 2);
    const words = sub.text.split(' ');
    const midWord = Math.ceil(words.length / 2);
    const first: SubtitleEntry = { ...sub, id: `sub-${Date.now()}-a`, text: words.slice(0, midWord).join(' '), endMs: midMs };
    const second: SubtitleEntry = { ...sub, id: `sub-${Date.now()}-b`, text: words.slice(midWord).join(' '), startMs: midMs };
    setSubs(prev => [...prev.slice(0, index), first, second, ...prev.slice(index + 1)]);
  }, [subs]);

  const applyGlobalStyle = useCallback(() => {
    const pos = POSITIONS[globalStyle.positionPreset];
    setSubs(prev => prev.map(s => ({
      ...s,
      fontSize: globalStyle.fontSize,
      fontFamily: globalStyle.fontFamily,
      color: globalStyle.color,
      backgroundColor: globalStyle.styleMode === 'box' ? globalStyle.backgroundColor : undefined,
      strokeColor: globalStyle.strokeColor,
      strokeWidth: globalStyle.styleMode === 'outline' ? Math.max(globalStyle.strokeWidth, 4) : globalStyle.strokeWidth,
      x: pos.x,
      y: pos.y,
    })));
  }, [globalStyle]);

  const seekTo = useCallback((ms: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = ms / 1000;
      setCurrentTime(ms);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  }, []);

  // Visible subtitles at current time
  const visibleSubs = subs.filter(s => currentTime >= s.startMs && currentTime <= s.endMs);

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    const frac = Math.floor((ms % 1000) / 100);
    return `${m}:${String(sec).padStart(2, '0')}.${frac}`;
  };

  const handleSave = () => { onSave(subs); };
  const handleBurn = () => { onBurn(subs); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth sx={{ '& .MuiDialog-paper': { height: '90vh', maxWidth: '1200px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
        <Typography variant="h6" fontSize="1rem">Caption Editor</Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 2, p: 2, overflow: 'hidden' }}>
        {/* Left: Video Preview */}
        <Box sx={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              src={videoUrl}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              playsInline
              crossOrigin="anonymous"
            />
            {/* CSS subtitle overlay — above the video */}
            {visibleSubs.map((sub) => (
              <Box
                key={sub.id}
                sx={{
                  position: 'absolute',
                  zIndex: 10,
                  left: sub.x !== undefined ? `${sub.x}%` : '50%',
                  top: sub.y !== undefined ? `${sub.y}%` : '85%',
                  transform: sub.x !== undefined ? 'none' : 'translateX(-50%)',
                  px: sub.backgroundColor ? 2 : 0,
                  py: sub.backgroundColor ? 0.5 : 0,
                  borderRadius: 0.5,
                  backgroundColor: sub.backgroundColor || 'transparent',
                  color: sub.color || '#ffffff',
                  fontSize: `${Math.max(10, (sub.fontSize || 48) / 3)}px`,
                  fontFamily: sub.fontFamily || 'Montserrat, sans-serif',
                  fontWeight: 700,
                  WebkitTextStroke: !sub.backgroundColor ? `${Math.min(4, (sub.strokeWidth || 2) / 2)}px ${sub.strokeColor || '#000'}` : undefined,
                  textShadow: sub.backgroundColor
                    ? `${sub.strokeWidth || 2}px ${sub.strokeWidth || 2}px 0px ${sub.strokeColor || '#000'}`
                    : `-1px -1px 0 ${sub.strokeColor || '#000'}, 1px -1px 0 ${sub.strokeColor || '#000'}, -1px 1px 0 ${sub.strokeColor || '#000'}, 1px 1px 0 ${sub.strokeColor || '#000'}`,
                  whiteSpace: 'pre-wrap',
                  pointerEvents: 'none',
                  maxWidth: '90%',
                  textAlign: 'center',
                }}
              >
                {sub.text}
              </Box>
            ))}
          </Box>

          {/* Playback controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={togglePlay}>
              {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 50 }}>{formatTime(currentTime)}</Typography>
            <Slider
              size="small"
              value={currentTime}
              min={0}
              max={duration || 10000}
              step={100}
              onChange={(_, v) => seekTo(v as number)}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 50 }}>{formatTime(duration)}</Typography>
          </Box>

          {/* Global style controls */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" fontWeight={600}>Global Style</Typography>
              <Button size="small" onClick={applyGlobalStyle} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                Apply to All
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Font</InputLabel>
                <Select
                  value={globalStyle.fontFamily}
                  label="Font"
                  onChange={(e) => setGlobalStyle(s => ({ ...s, fontFamily: e.target.value }))}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {FONTS.map(f => <MenuItem key={f} value={f} sx={{ fontSize: '0.75rem' }}>{f}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                type="number"
                size="small"
                label="Size"
                value={globalStyle.fontSize}
                onChange={(e) => setGlobalStyle(s => ({ ...s, fontSize: Number(e.target.value) }))}
                sx={{ width: 70, '& input': { fontSize: '0.75rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption">Text</Typography>
                <input type="color" value={globalStyle.color} onChange={(e) => setGlobalStyle(s => ({ ...s, color: e.target.value }))} style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }} />
              </Box>
              <FormControl size="small" sx={{ minWidth: 95 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Style</InputLabel>
                <Select
                  value={globalStyle.styleMode}
                  label="Style"
                  onChange={(e) => setGlobalStyle(s => ({ ...s, styleMode: e.target.value as 'box' | 'outline' }))}
                  sx={{ fontSize: '0.75rem' }}
                >
                  <MenuItem value="box" sx={{ fontSize: '0.75rem' }}>Box</MenuItem>
                  <MenuItem value="outline" sx={{ fontSize: '0.75rem' }}>Outline</MenuItem>
                </Select>
              </FormControl>
              {globalStyle.styleMode === 'box' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">BG</Typography>
                  <input type="color" value={(globalStyle.backgroundColor || '#000000').slice(0, 7)} onChange={(e) => setGlobalStyle(s => ({ ...s, backgroundColor: e.target.value + 'aa' }))} style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption">Stroke</Typography>
                    <input type="color" value={globalStyle.strokeColor} onChange={(e) => setGlobalStyle(s => ({ ...s, strokeColor: e.target.value }))} style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }} />
                  </Box>
                  <TextField
                    type="number"
                    size="small"
                    label="Width"
                    value={globalStyle.strokeWidth}
                    onChange={(e) => setGlobalStyle(s => ({ ...s, strokeWidth: Math.max(1, Number(e.target.value)) }))}
                    sx={{ width: 75, '& input': { fontSize: '0.75rem' } }}
                    InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                </>
              )}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Position</InputLabel>
                <Select
                  value={globalStyle.positionPreset}
                  label="Position"
                  onChange={(e) => setGlobalStyle(s => ({ ...s, positionPreset: Number(e.target.value) }))}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {POSITIONS.map((p, i) => <MenuItem key={i} value={i} sx={{ fontSize: '0.75rem' }}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Right: Subtitle List */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">{subs.length} Caption{subs.length !== 1 ? 's' : ''}</Typography>
            <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={addSub} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Add
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {subs.map((sub, i) => {
              const isActive = currentTime >= sub.startMs && currentTime <= sub.endMs;
              const isSelected = i === selected;
              return (
                <Box
                  key={sub.id}
                  onClick={() => { setSelected(i); seekTo(sub.startMs); }}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : isActive ? 'success.main' : 'divider',
                    bgcolor: isActive ? 'action.hover' : 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                >
                  {/* Timing row */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                    <Chip size="small" label={`#${i + 1}`} sx={{ height: 18, fontSize: '0.65rem' }} />
                    <TextField
                      size="small"
                      type="number"
                      value={sub.startMs}
                      onChange={(e) => updateSub(i, { startMs: Math.max(0, Number(e.target.value)) })}
                      onClick={(e) => e.stopPropagation()}
                      InputProps={{ inputProps: { step: 100 } }}
                      sx={{ width: 80, '& input': { fontSize: '0.7rem', py: 0.25 } }}
                    />
                    <Typography variant="caption" color="text.secondary">→</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={sub.endMs}
                      onChange={(e) => updateSub(i, { endMs: Math.max(sub.startMs, Number(e.target.value)) })}
                      onClick={(e) => e.stopPropagation()}
                      InputProps={{ inputProps: { step: 100 } }}
                      sx={{ width: 80, '& input': { fontSize: '0.7rem', py: 0.25 } }}
                    />
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.6rem' }}>
                      {((sub.endMs - sub.startMs) / 1000).toFixed(1)}s
                    </Typography>
                    <TooltipIconButton size="small" onClick={(e) => { e.stopPropagation(); splitSub(i); }} title="Split">
                      <CallSplit sx={{ fontSize: 14 }} />
                    </TooltipIconButton>
                    <TooltipIconButton size="small" onClick={(e) => { e.stopPropagation(); deleteSub(i); }} title="Delete">
                      <Delete sx={{ fontSize: 14 }} />
                    </TooltipIconButton>
                  </Box>

                  {/* Text */}
                  <TextField
                    size="small"
                    value={sub.text}
                    onChange={(e) => updateSub(i, { text: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    multiline
                    maxRows={2}
                    fullWidth
                    placeholder="Caption text..."
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                  />

                  {/* Per-subtitle style (when selected) */}
                  {isSelected && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Select
                        size="small"
                        value={sub.fontFamily || 'Montserrat'}
                        onChange={(e) => updateSub(i, { fontFamily: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontSize: '0.7rem', height: 28, minWidth: 100 }}
                      >
                        {FONTS.map(f => <MenuItem key={f} value={f} sx={{ fontSize: '0.7rem' }}>{f}</MenuItem>)}
                      </Select>
                      <TextField
                        type="number"
                        size="small"
                        value={sub.fontSize || 48}
                        onChange={(e) => updateSub(i, { fontSize: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 55, '& input': { fontSize: '0.7rem', py: 0.25 } }}
                      />
                      <input
                        type="color"
                        value={sub.color || '#ffffff'}
                        onChange={(e) => updateSub(i, { color: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        title="Text color"
                        style={{ width: 22, height: 22, border: 'none', cursor: 'pointer' }}
                      />
                      {sub.backgroundColor ? (
                        <input
                          type="color"
                          value={(sub.backgroundColor || '#000000aa').slice(0, 7)}
                          onChange={(e) => updateSub(i, { backgroundColor: e.target.value + 'aa' })}
                          onClick={(e) => e.stopPropagation()}
                          title="Background"
                          style={{ width: 22, height: 22, border: 'none', cursor: 'pointer' }}
                        />
                      ) : (
                        <input
                          type="color"
                          value={sub.strokeColor || '#000000'}
                          onChange={(e) => updateSub(i, { strokeColor: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          title="Stroke"
                          style={{ width: 22, height: 22, border: 'none', cursor: 'pointer' }}
                        />
                      )}
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateSub(i, sub.backgroundColor
                            ? { backgroundColor: undefined, strokeWidth: Math.max(sub.strokeWidth || 2, 4) }
                            : { backgroundColor: '#000000aa' });
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.65rem', minWidth: 55, px: 0.5, py: 0, height: 24 }}
                      >
                        {sub.backgroundColor ? 'Box' : 'Outline'}
                      </Button>
                    </Box>
                  )}
                  {/* Per-subtitle position sliders when selected */}
                  {isSelected && (
                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ minWidth: 14, fontSize: '0.65rem' }}>X</Typography>
                        <Slider
                          size="small"
                          value={sub.x ?? 50}
                          min={0}
                          max={100}
                          step={1}
                          onChange={(_, v) => updateSub(i, { x: v as number })}
                          sx={{ flex: 1, py: 0.5 }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 24, fontSize: '0.65rem', textAlign: 'right' }}>{sub.x ?? 50}%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ minWidth: 14, fontSize: '0.65rem' }}>Y</Typography>
                        <Slider
                          size="small"
                          value={sub.y ?? 85}
                          min={0}
                          max={100}
                          step={1}
                          onChange={(_, v) => updateSub(i, { y: v as number })}
                          sx={{ flex: 1, py: 0.5 }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 24, fontSize: '0.65rem', textAlign: 'right' }}>{sub.y ?? 85}%</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleSave} variant="outlined" sx={{ textTransform: 'none' }}>
          Save Captions
        </Button>
        <Button
          onClick={handleBurn}
          variant="contained"
          color="secondary"
          disabled={isBurning || subs.length === 0}
          startIcon={<LocalFireDepartment />}
          sx={{ textTransform: 'none' }}
        >
          {isBurning ? 'Burning...' : 'Burn into Video'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
